import { useState } from "react";

import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";

import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { Stepper } from "../../../../Components/Stepper";
import { type Step } from "../../../../Components/Stepper/Types";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useUserContext } from "../../../../Context/UserContext";
import { customersRoute } from "../../../../Navigation/core/utils";
import { useOnboardWiz } from "./hooks";
import ClientCredentialsStep from "./Steps/ClientCredentialsStep";
import TenantIdStep from "./Steps/TenantIdStep";
import { type FormState } from "./types";

export default function WizOnboarding() {
  const history = useHistory();
  const customerId = useCustomerId();
  const { userRoles } = useUserContext();
  const { isDoitEmployee } = useAuthContext();
  const hasPermissionToOnboard = userRoles?.billingProfilesAdmin || isDoitEmployee;
  const showErrorSnackbar = useErrorSnackbar();
  const { onboard, onboarding } = useOnboardWiz();

  const [formState, setFormState] = useState<FormState>({
    clientId: "",
    clientSecret: "",
    orgId: "",
    orgName: "",
    connectionSuccess: false,
    connectionMessage: "",
    loading: false,
  });

  const returnToHomePage = () => {
    history.push(`${customersRoute(customerId)}/integrate/assets/wiz`);
  };

  const handleOnboard = async () => {
    const onboardingResult = await onboard(formState.orgId, formState.orgName, {
      clientId: formState.clientId,
      clientSecret: formState.clientSecret,
    });

    if (onboardingResult.success) {
      returnToHomePage();
    } else {
      setFormState({
        ...formState,
        connectionSuccess: onboardingResult.success,
        connectionMessage: onboardingResult.message,
      });
    }
  };

  const handleChange = (field: "clientId" | "clientSecret" | "orgId" | "orgName", value: string) => {
    setFormState({
      ...formState,
      [field]: value,
      connectionSuccess: false,
      connectionMessage: "",
    });
  };

  const steps: Step[] = [
    {
      children: (
        <TenantIdStep
          tenantId={formState.orgId}
          orgName={formState.orgName}
          onTenantIdChange={(value: string) => {
            handleChange("orgId", value);
          }}
          onOrgNameChange={(value: string) => {
            handleChange("orgName", value);
          }}
        />
      ),
      label: "Tenant ID",
      order: 0,
      required: true,
      state: formState.orgId !== "" ? "complete" : "incomplete",
    },
    {
      children: <ClientCredentialsStep formState={formState} onChange={setFormState} />,
      label: "Client Credentials",
      order: 1,
      required: true,
      state:
        formState.clientId !== "" && formState.clientSecret !== "" && formState.connectionSuccess
          ? "complete"
          : "incomplete",
    },
  ];

  return (
    <Box mt={6} mb={9}>
      <Stepper
        loading={formState.loading || onboarding}
        onCancel={returnToHomePage}
        onSubmit={handleOnboard}
        nextButtonClickHandler={async () => {
          if (!hasPermissionToOnboard) {
            showErrorSnackbar("Permission Denied: Billing Profile Admin role required");
            return false;
          }
          return true;
        }}
        steps={steps}
        submitButtonLabel={formState.connectionSuccess ? "Finish" : undefined}
        contentSx={{
          mt: 4,
          mb: 3,
        }}
        footerMaxWidth={860}
        maxWidth={1600}
      />
    </Box>
  );
}
